/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {themedClassName} from '../../theme/helpers';
import {useThemeContext} from '../../theme/useThemeContext';
import {useIsAssetDetailsTile} from './assetDetailsTileContext';
import sharedStyles from './styles';

export type WidgetContainerProps = {
  children: ReactNode;
};

/**
 * Wraps a Smarter Trailer widget's icon and description in the shared
 * widget container - a centered legacy column, or a left-aligned
 * current-theme tile row inside the Asset Details drawer (ION-7522).
 *
 * ```typescript
 * import {WidgetContainer} from '@ion/dls/src/components/smarterTrailer/WidgetContainer';
 * ...
 *
 * <WidgetContainer>
 *   <BrakeChamberIcon />
 *   <WidgetDescription label='Brake Chamber'
 *     value='Released'
 *     messageDate='January 1, 2026'
 *     messageTime='8:20 AM' />
 * </WidgetContainer>
 * ```
 *
 * @param children The widget's icon and description elements.
 * @returns        The themed widget container `div`.
 */
export const WidgetContainer = ({children}: WidgetContainerProps) => {
  const classes       = sharedStyles();
  const {isCurrent}   = useThemeContext();
  const isTile        = useIsAssetDetailsTile();
  const isCurrentTile = isCurrent && isTile;

  return (
    <div className={themedClassName(classes.container, classes.currentContainer, isCurrentTile)}>
      {children}
    </div>
  );
};

export default WidgetContainer;
